import { useGetOrderDetail } from "@/lib/api/risk/brokerage/tt/v2";
import { TtOrderEvent, TtOrderMetadata, TtOrderSnapshot } from "@/lib/api/risk/brokerage/tt";
import { CollapsibleSection } from "./CollapsibleSection";
import { OrderContextHeader } from "./OrderContextHeader";
import { OrderEventsSection } from "./OrderEventsSection";
import { AssociatedMetadataSection } from "./AssociatedMetadataSection";
import { AttachOrderForm } from "../attach-form/AttachOrderForm";

export interface OrderDetailContentProps {
  guidOrderId: string | null;
  wasResolved: boolean;
  /** When false, hide the AttachOrderForm section entirely (read-only). Default true. */
  showAttachForm?: boolean;
  /**
   * When true, render a read-only summary of the AssociatedMetaData between
   * the Order Context and Order Events sections. Default false.
   */
  showAssociatedMetadata?: boolean;
}

/**
 * Inner content of the order detail view. Loads the order detail and renders
 * the order context, optional metadata summary, optional attach form, and
 * order events. Intentionally renders no chrome (no header bar, no close
 * button) so it can be hosted inside either a side panel or a modal.
 */
export function OrderDetailContent({
  guidOrderId,
  wasResolved,
  showAttachForm = true,
  showAssociatedMetadata = false,
}: OrderDetailContentProps) {
  const detailResult = useGetOrderDetail(guidOrderId ?? "", { enabled: !!guidOrderId });
  const detail = detailResult.data?.data;

  return (
    <PanelBody
      guidOrderId={guidOrderId}
      wasResolved={wasResolved}
      isLoading={detailResult.isLoading}
      isError={detailResult.isError}
      error={detailResult.error}
      detail={detail}
      showAttachForm={showAttachForm}
      showAssociatedMetadata={showAssociatedMetadata}
    />
  );
}

interface PanelBodyProps {
  guidOrderId: string | null;
  wasResolved: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  detail:
    | {
        snapshot: TtOrderSnapshot | null;
        events: TtOrderEvent[];
        metadata: TtOrderMetadata | null;
      }
    | undefined;
  showAttachForm: boolean;
  showAssociatedMetadata: boolean;
}

function PanelBody({
  guidOrderId,
  wasResolved,
  isLoading,
  isError,
  error,
  detail,
  showAttachForm,
  showAssociatedMetadata,
}: PanelBodyProps) {
  if (guidOrderId == null) {
    return (
      <p className="text-sm text-muted-foreground">
        Select an order from the table to enrich its data.
      </p>
    );
  }

  if (showAttachForm && wasResolved && !detail?.snapshot) {
    return (
      <p className="text-sm text-muted-foreground">
        ✓ Saved. Select another order from the table.
      </p>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading order details...</p>;
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600">
        Failed to load order details
        {error instanceof Error ? `: ${error.message}` : ""}
      </p>
    );
  }

  if (!detail || !detail.snapshot) {
    return <p className="text-sm text-muted-foreground">No snapshot found for this order.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <CollapsibleSection title="Order Context" defaultOpen={true}>
        <OrderContextHeader snapshot={detail.snapshot} />
      </CollapsibleSection>

      {showAssociatedMetadata && (
        <CollapsibleSection title="Associated Metadata" defaultOpen={true}>
          <AssociatedMetadataSection metadata={detail.metadata} />
        </CollapsibleSection>
      )}

      {showAttachForm && (
        <AttachOrderForm
          key={guidOrderId}
          guidOrderId={guidOrderId}
          snapshot={detail.snapshot}
          existingMetadata={detail.metadata}
        />
      )}

      <CollapsibleSection title={`Order Events (${detail.events.length})`} defaultOpen={false}>
        <OrderEventsSection
          events={detail.events}
          metadata={detail.metadata}
          guidOrderId={guidOrderId}
        />
      </CollapsibleSection>
    </div>
  );
}
